import { useCallback, useEffect, useMemo, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { adminStorage } from "../../features/admin/service";
import { apiClient } from "../../lib/http/apiClient";
import { parseApiError } from "../../lib/http/apiErrors";
import ConfirmModal from "../../components/admin/ConfirmModal";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import Spinner from "../../components/ui/Spinner";
import RefreshButton from "../../components/admin/RefreshButton";
import { useAdminToast } from "../../context/AdminToastContext";

interface Category {
  id?: string;
  slug: string;
  name: string;
  group_slug: string | null;
  display_group: string | null;
  parent_slug: string | null;
  level: number;
  item_types: string[];
  is_promotional: boolean;
  description: string | null;
  keywords: string[];
  icon: string | null;
  image_url: string | null;
  video_url: string | null;
  is_active: boolean;
  is_promoted: boolean;
  sort_order: number;
  is_priority_fixed: boolean;
}

/**
 * Priority numbers not yet taken by another fixed row in the same scope
 * (same group_slug for subcategories, promoted list, or top-level groups),
 * so the admin can only pick a slot that's actually free.
 */
function availablePrioritySlots(cat: Category, allCategories: Category[]): number[] {
  // A group anchor row (name === display_group — same test used elsewhere in
  // this codebase, e.g. backend's _compute_categories_summary) represents the
  // whole group in the top-level groups list, not a member of its own
  // group's subcategory scope — it must compete with other group anchors,
  // not with its own children, even though it carries a group_slug value.
  const isGroupAnchor = (c: Category) => {
    const n = (c.name ?? "").trim().toLowerCase();
    const dg = (c.display_group ?? "").trim().toLowerCase();
    return !!n && !!dg && n === dg;
  };
  const scopeOf = (c: Category) =>
    c.is_promoted ? "promoted" : c.group_slug && !isGroupAnchor(c) ? `group:${c.group_slug}` : "top-level";
  const scope = scopeOf(cat);
  const taken = new Set(
    allCategories
      .filter((c) => c.slug !== cat.slug && c.is_priority_fixed && scopeOf(c) === scope)
      .map((c) => c.sort_order)
  );
  const maxSlot = allCategories.filter((c) => scopeOf(c) === scope).length;
  const slots: number[] = [];
  for (let i = 1; i <= maxSlot + 1; i++) {
    if (!taken.has(i)) slots.push(i);
  }
  return slots;
}

/**
 * Canonical groups shown in the admin Groups tab. The first 7 are LOCKED to
 * the set the backend can route typed deals into (see GROUP_TO_COLLECTION in
 * backend/app/modules/deals/typed/validator.py) — creating an arbitrary new
 * typed-routable group is intentionally disabled, since a deal whose
 * group_slug has no typed collection cannot be saved. `transport` is treated
 * as an alias of `transfers`.
 *
 * `deals-offers`, `smart-picks`, and `media` are not typed-deal-routable —
 * they're editorial/AI groupings — but their category docs already exist
 * with real subcategories, so they're listed here too for label/icon/banner/
 * promoted management, same as the 7 routable groups.
 */
const CANONICAL_GROUPS: { slug: string; label: string }[] = [
  { slug: "stays", label: "Stays" },
  { slug: "packages", label: "Packages & Trips" },
  { slug: "flights", label: "Flights" },
  { slug: "cruises", label: "Cruises" },
  { slug: "transfers", label: "Transfers" },
  { slug: "experiences", label: "Experiences" },
  { slug: "services", label: "Travel Services" },
  { slug: "deals-offers", label: "Deals & Offers" },
  { slug: "smart-picks", label: "Smart Picks" },
  { slug: "media", label: "Media" },
];
const CANONICAL_SLUGS = new Set(CANONICAL_GROUPS.map((g) => g.slug));

/** Allowed item types — mirrors CategoryItemType in
 * backend/app/modules/rag/deal_categories_data.py. A category's item_types
 * gate which item_type a deal under it may carry (validator Rule 4). */
const ITEM_TYPES = [
  "DEAL", "HOTEL", "PACKAGE", "FLIGHT", "TRANSFER", "CRUISE", "EXPERIENCE",
  "SERVICE", "AI_MEDIA",
];
const DEFAULT_GROUP_LABEL: Record<string, string> = Object.fromEntries(
  CANONICAL_GROUPS.map((g) => [g.slug, g.label]),
);

/** Fold the legacy `transport` slug into `transfers`. */
const normGroup = (g: string | null | undefined): string => {
  const s = (g ?? "").trim().toLowerCase();
  return s === "transport" ? "transfers" : s;
};

type Tab = "groups" | "subcategories";

const emptySubForm = () => ({
  slug: "",
  name: "",
  group_slug: "",
  icon: "",
  description: "",
  item_types: [] as string[],
  keywords: "",
  sort_order: 100,
  is_active: true,
  is_promoted: false,
  is_priority_fixed: false,
  image_url: null as string | null,
  video_url: null as string | null,
});

const emptyGroupForm = () => ({
  slug: "",
  label: "",
  icon: "",
  sort_order: 100,
  is_active: true,
  is_promoted: false,
  is_priority_fixed: false,
  image_url: null as string | null,
  video_url: null as string | null,
});

/** Sentinel editingGroup value meaning "creating a brand-new, non-canonical
 * group" — as opposed to editing one of the fixed CANONICAL_GROUPS. New
 * groups created this way can only ever be used for SECONDARY categorization
 * on a deal (see secondary_slugs) — they are not added to GROUP_TO_COLLECTION
 * in the backend, so a deal can never set one as its primary group_slug; the
 * backend's Rule 6 routing check enforces this independent of anything here. */
const NEW_GROUP_SENTINEL = "__new_group__";

function adminHeaders() {
  const token = adminStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function AdminCategoriesPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { showError, showSuccess } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);
  useEffect(() => { if (success) showSuccess(success); }, [success, showSuccess]);
  const [tab, setTab] = useState<Tab>("groups");

  // Subcategory form/list state
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [subForm, setSubForm] = useState(emptySubForm());
  const [pendingSubMediaFile, setPendingSubMediaFile] = useState<File | null>(null);
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [promotedFilter, setPromotedFilter] = useState<"" | "promoted" | "not_promoted">("");
  const [selectedSlugs, setSelectedSlugs] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<Category | null>(null);
  const [previewMedia, setPreviewMedia] = useState<{ imageUrl: string | null; videoUrl: string | null } | null>(null);

  // Group form state
  const [editingGroup, setEditingGroup] = useState<string | null>(null);
  const [groupForm, setGroupForm] = useState(emptyGroupForm());
  const [demoteTarget, setDemoteTarget] = useState<{ slug: string; label: string } | null>(null);
  useBodyScrollLock(!!demoteTarget || !!previewMedia);
  const [demoteDestGroup, setDemoteDestGroup] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<Category[]>("/api/admin/v2/categories", { headers: adminHeaders() });
      setCategories(res.data);
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  /** A category is a "group anchor" when its name matches its display group,
   * or when its slug is itself a canonical group slug. */
  const isAnchor = useCallback((c: Category) => {
    if (CANONICAL_SLUGS.has(normGroup(c.slug))) return true;
    const n = (c.name ?? "").trim().toLowerCase();
    const dg = (c.display_group ?? "").trim().toLowerCase();
    return !!n && !!dg && n === dg;
  }, []);

  const findAnchor = useCallback(
    (groupSlug: string): Category | undefined =>
      categories.find((c) => isAnchor(c) && normGroup(c.group_slug || c.slug) === groupSlug) ||
      categories.find((c) => normGroup(c.slug) === groupSlug),
    [categories, isAnchor],
  );

  // Build the Groups tab rows from the canonical set + their anchor docs + member counts.
  // Sorted by sort_order ascending (sort_order=1 renders first / array[0]) — matches
  // the backend's own ordering (repository.get_all sorts "sort_order", 1) so the admin
  // table reflects the same order buyers see.
  const groupRows = useMemo(() => {
    const rows = CANONICAL_GROUPS.map((g) => {
      const anchor = findAnchor(g.slug);
      const members = categories.filter((c) => !isAnchor(c) && normGroup(c.group_slug) === g.slug);
      return {
        slug: g.slug,
        label: anchor?.display_group || anchor?.name || DEFAULT_GROUP_LABEL[g.slug],
        icon: anchor?.icon ?? "",
        sort_order: anchor?.sort_order ?? 100,
        is_active: anchor?.is_active ?? true,
        is_promoted: anchor?.is_promoted ?? false,
        is_priority_fixed: anchor?.is_priority_fixed ?? false,
        image_url: anchor?.image_url ?? null,
        video_url: anchor?.video_url ?? null,
        anchorSlug: anchor?.slug ?? null,
        memberCount: members.length,
      };
    });
    return rows.sort((a, b) => a.sort_order - b.sort_order);
  }, [categories, findAnchor, isAnchor]);

  // Custom (non-canonical) groups: anchor docs whose group_slug isn't one of
  // the 7 typed-routable groups nor the 3 editorial ones. These are
  // secondary-only — the backend routing gate (GROUP_TO_COLLECTION) never
  // recognizes them as a primary group_slug, so deals can only attach them
  // via secondary_slugs.
  const customGroupRows = useMemo(() => {
    const anchors = categories.filter((c) => isAnchor(c) && !CANONICAL_SLUGS.has(normGroup(c.group_slug || c.slug)));
    const rows = anchors.map((anchor) => {
      const slug = normGroup(anchor.group_slug || anchor.slug);
      const members = categories.filter((c) => !isAnchor(c) && normGroup(c.group_slug) === slug);
      return {
        slug,
        label: anchor.display_group || anchor.name,
        icon: anchor.icon ?? "",
        sort_order: anchor.sort_order ?? 100,
        is_active: anchor.is_active,
        is_promoted: anchor.is_promoted,
        is_priority_fixed: anchor.is_priority_fixed ?? false,
        image_url: anchor.image_url,
        video_url: anchor.video_url,
        anchorSlug: anchor.slug,
        memberCount: members.length,
      };
    });
    return rows.sort((a, b) => a.sort_order - b.sort_order);
  }, [categories, isAnchor]);

  // Subcategories = everything that is not a group anchor.
  const subcategories = useMemo(
    () => categories.filter((c) => !isAnchor(c)),
    [categories, isAnchor],
  );

  const filteredSubs = subcategories
    .filter((c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.slug.toLowerCase().includes(search.toLowerCase()) ||
      (c.group_slug ?? "").toLowerCase().includes(search.toLowerCase())
    )
    .filter((c) => !groupFilter || normGroup(c.group_slug) === groupFilter)
    .filter((c) =>
      promotedFilter === "" ? true : promotedFilter === "promoted" ? c.is_promoted : !c.is_promoted
    );

  // ── Subcategory handlers ──────────────────────────────────────────────
  const handleEditSub = (cat: Category) => {
    setEditingSlug(cat.slug);
    setPendingSubMediaFile(null);
    setSubForm({
      slug: cat.slug,
      name: cat.name,
      group_slug: normGroup(cat.group_slug),
      icon: cat.icon ?? "",
      description: cat.description ?? "",
      item_types: cat.item_types ?? [],
      keywords: (cat.keywords ?? []).join(", "),
      sort_order: cat.sort_order ?? 100,
      is_active: cat.is_active,
      is_promoted: cat.is_promoted,
      is_priority_fixed: cat.is_priority_fixed,
      image_url: cat.image_url,
      video_url: cat.video_url,
    });
    setError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNewSub = () => {
    setEditingSlug(null);
    setSubForm(emptySubForm());
    setPendingSubMediaFile(null);
    setError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmitSub = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subForm.slug.trim() || !subForm.name.trim()) {
      setError("Slug and name are required.");
      return;
    }
    setSaving("subform");
    setError(null);
    try {
      const groupSlug = subForm.group_slug.trim() || null;
      const groupLabel = groupSlug
        ? (findAnchor(groupSlug)?.display_group || DEFAULT_GROUP_LABEL[groupSlug] || groupSlug)
        : null;
      const body = {
        name: subForm.name.trim(),
        group_slug: groupSlug,
        // display_group + parent_slug + level are derived from the chosen group.
        display_group: groupLabel,
        parent_slug: groupSlug,
        level: 2,
        icon: subForm.icon.trim() || null,
        description: subForm.description.trim() || null,
        item_types: subForm.item_types,
        keywords: subForm.keywords.split(",").map((s) => s.trim()).filter(Boolean),
        sort_order: Number(subForm.sort_order) || 100,
        is_active: subForm.is_active,
        is_promoted: subForm.is_promoted,
        is_priority_fixed: subForm.is_priority_fixed,
      };

      if (editingSlug) {
        await apiClient.put(`/api/admin/v2/categories/${editingSlug}`, body, { headers: adminHeaders() });
        setSuccess(`Updated "${subForm.name}".`);
      } else {
        const newSlug = subForm.slug.trim();
        await apiClient.post("/api/admin/v2/categories", { ...body, slug: newSlug }, { headers: adminHeaders() });
        setSuccess(`Created "${subForm.name}".`);
        if (pendingSubMediaFile) {
          await handleSubMediaUpload(newSlug, pendingSubMediaFile);
          setPendingSubMediaFile(null);
        }
      }
      setSubForm(emptySubForm());
      setEditingSlug(null);
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  const handleToggleActive = async (cat: Category) => {
    setSaving(cat.slug + "is_active");
    try {
      await apiClient.put(`/api/admin/v2/categories/${cat.slug}`, { is_active: !cat.is_active }, { headers: adminHeaders() });
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  const handleTogglePromoted = async (cat: Category) => {
    setSaving(cat.slug + "is_promoted");
    try {
      await apiClient.put(`/api/admin/v2/categories/${cat.slug}`, { is_promoted: !cat.is_promoted }, { headers: adminHeaders() });
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  const handleToggleFixedPriority = async (cat: Category) => {
    setSaving(cat.slug + "is_priority_fixed");
    try {
      const enabling = !cat.is_priority_fixed;
      // When enabling, most categories are still sitting at the schema
      // default sort_order=100, so send the first actually-free slot in this
      // category's scope instead of leaving sort_order unset (which would
      // collide with whatever else is also still at 100 and 400 with a
      // confusing "Priority 100 is already fixed" error).
      const body: { is_priority_fixed: boolean; sort_order?: number } = {
        is_priority_fixed: enabling,
      };
      if (enabling) {
        const [firstSlot] = availablePrioritySlots(cat, categories);
        if (firstSlot !== undefined) body.sort_order = firstSlot;
      }
      await apiClient.put(
        `/api/admin/v2/categories/${cat.slug}`,
        body,
        { headers: adminHeaders() }
      );
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  const handleSetFixedPriority = async (cat: Category, newSortOrder: number) => {
    setSaving(cat.slug + "sort_order");
    try {
      await apiClient.put(
        `/api/admin/v2/categories/${cat.slug}`,
        { sort_order: newSortOrder },
        { headers: adminHeaders() }
      );
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  const toggleSelected = (slug: string) => {
    setSelectedSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const handleBulkSetPromoted = async (promoted: boolean) => {
    if (selectedSlugs.size === 0) return;
    setSaving("bulk-promote");
    setError(null);
    try {
      await Promise.all(
        Array.from(selectedSlugs).map((slug) =>
          apiClient.put(`/api/admin/v2/categories/${slug}`, { is_promoted: promoted }, { headers: adminHeaders() })
        ),
      );
      setSuccess(`${promoted ? "Promoted" : "Unpromoted"} ${selectedSlugs.size} subcategor${selectedSlugs.size === 1 ? "y" : "ies"}.`);
      setSelectedSlugs(new Set());
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(deleteTarget.slug);
    try {
      // Hard delete — permanently removes the document from MongoDB.
      await apiClient.delete(`/api/admin/v2/categories/${deleteTarget.slug}?hard=true`, { headers: adminHeaders() });
      setDeleteTarget(null);
      setSuccess(`"${deleteTarget.name}" permanently deleted.`);
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  // ── Group handlers ────────────────────────────────────────────────────
  const handleEditGroup = (g: typeof groupRows[number]) => {
    setEditingGroup(g.slug);
    setGroupForm({
      slug: g.slug,
      label: g.label,
      icon: g.icon,
      sort_order: g.sort_order,
      is_active: g.is_active,
      is_promoted: g.is_promoted,
      is_priority_fixed: g.is_priority_fixed,
      image_url: g.image_url,
      video_url: g.video_url,
    });
    setError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelGroup = () => {
    setEditingGroup(null);
    setGroupForm(emptyGroupForm());
    setError(null);
  };

  /** Start creating a brand-new, secondary-only group from scratch. */
  const handleNewGroup = () => {
    setEditingGroup(NEW_GROUP_SENTINEL);
    setGroupForm(emptyGroupForm());
    setError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /** Promote an existing subcategory into its own new secondary-only group
   * (pre-fills slug/label from the subcategory). The subcategory itself is
   * left untouched — admin can re-point its group_slug at the new group
   * afterwards via Edit if desired. */
  const handlePromoteToGroup = (cat: Category) => {
    setEditingGroup(NEW_GROUP_SENTINEL);
    setGroupForm({
      ...emptyGroupForm(),
      slug: cat.slug,
      label: cat.name,
      icon: cat.icon ?? "",
    });
    setError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmitGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingGroup) return;
    const isNew = editingGroup === NEW_GROUP_SENTINEL;
    if (!groupForm.label.trim()) {
      setError("Group label is required.");
      return;
    }
    if (isNew && !groupForm.slug.trim()) {
      setError("Group slug is required.");
      return;
    }
    setSaving("groupform");
    setError(null);
    try {
      const groupSlug = isNew ? groupForm.slug.trim().toLowerCase() : editingGroup;
      if (isNew && CANONICAL_SLUGS.has(groupSlug)) {
        setError(`"${groupSlug}" is a canonical group slug and can't be reused.`);
        setSaving(null);
        return;
      }
      const label = groupForm.label.trim();
      const anchor = findAnchor(groupSlug);
      const anchorBody = {
        name: label,
        display_group: label,
        group_slug: groupSlug,
        level: 1,
        icon: groupForm.icon.trim() || null,
        sort_order: Number(groupForm.sort_order) || 100,
        is_active: groupForm.is_active,
        is_promoted: groupForm.is_promoted,
        is_priority_fixed: groupForm.is_priority_fixed,
      };
      if (anchor) {
        await apiClient.put(`/api/admin/v2/categories/${anchor.slug}`, anchorBody, { headers: adminHeaders() });
      } else {
        await apiClient.post(
          "/api/admin/v2/categories",
          { ...anchorBody, slug: groupSlug },
          { headers: adminHeaders() },
        );
      }
      // Cascade the display label to every member so buyer-facing grouping stays consistent.
      const members = categories.filter((c) => !isAnchor(c) && normGroup(c.group_slug) === groupSlug);
      await Promise.all(
        members
          .filter((m) => (m.display_group ?? "") !== label)
          .map((m) => apiClient.put(`/api/admin/v2/categories/${m.slug}`, { display_group: label }, { headers: adminHeaders() })),
      );
      setSuccess(anchor ? `Updated group "${label}".` : `Created new secondary-only group "${label}".`);
      handleCancelGroup();
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  const handleToggleGroupPromoted = async (g: typeof groupRows[number]) => {
    if (!g.anchorSlug) return;
    setSaving(g.slug + "group-is_promoted");
    setError(null);
    try {
      await apiClient.put(`/api/admin/v2/categories/${g.anchorSlug}`, { is_promoted: !g.is_promoted }, { headers: adminHeaders() });
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  /** Undo a group promotion — turns a custom group's anchor doc back into a
   * plain subcategory nested under `demoteDestGroup`. */
  const handleDemoteToSubcategory = async () => {
    if (!demoteTarget || !demoteDestGroup) return;
    setSaving("demote");
    setError(null);
    try {
      const destLabel = findAnchor(demoteDestGroup)?.display_group || DEFAULT_GROUP_LABEL[demoteDestGroup] ||
        customGroupRows.find((c) => c.slug === demoteDestGroup)?.label || demoteDestGroup;
      await apiClient.put(`/api/admin/v2/categories/${demoteTarget.slug}`, {
        group_slug: demoteDestGroup,
        display_group: destLabel,
        parent_slug: demoteDestGroup,
        level: 2,
      }, { headers: adminHeaders() });
      setSuccess(`"${demoteTarget.label}" is now a subcategory under "${destLabel}".`);
      setDemoteTarget(null);
      setDemoteDestGroup("");
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  const handleGroupMediaUpload = async (groupSlug: string, file: File) => {
    setSaving(`${groupSlug}-media`);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiClient.post(
        `/api/categories/groups/${groupSlug}/hero-image`,
        formData,
        { headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" } },
      );
      setGroupForm((f) => ({
        ...f,
        image_url: res.data.image_url ?? f.image_url,
        video_url: res.data.video_url ?? null,
      }));
      setSuccess(`Updated media for group "${groupSlug}".`);
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  const handleSubMediaUpload = async (slug: string, file: File) => {
    setSaving(`${slug}-media`);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiClient.post(
        `/api/categories/${slug}/hero-image`,
        formData,
        { headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" } },
      );
      setSubForm((f) => ({
        ...f,
        image_url: res.data.image_url ?? f.image_url,
        video_url: res.data.video_url ?? null,
      }));
      setSuccess(`Updated media for "${slug}".`);
      await load();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  const inputCls =
    "w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50";

  return (
    <>
    <div>
      <AdminPageHeader
        title="Categories"
        subtitle={
          <>
            Manage top-level groups and the subcategories inside them. Changes apply everywhere
            categories are used (homepage, RAG routing, deal forms, AI categorization).
          </>
        }
        actions={<RefreshButton onRefresh={load} />}
      />

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-800">
        {(["groups", "subcategories"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); setError(null); setSuccess(null); }}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
              tab === t
                ? "border-indigo-500 text-white"
                : "border-transparent text-gray-500 hover:text-gray-300"
            }`}
          >
            {t === "groups" ? "Groups" : "Subcategories"}
          </button>
        ))}
      </div>

      {error && <div className="mb-4 px-4 py-3 bg-red-900/40 border border-red-700 rounded-xl text-red-300 text-sm">{error}</div>}
      {success && <div className="mb-4 px-4 py-3 bg-green-900/40 border border-green-700 rounded-xl text-green-300 text-sm">{success}</div>}

      {tab === "groups" ? (
        <>
          {/* Group edit form. The 7 typed-routable groups + 3 editorial ones
              are a fixed set (only editable). NEW_GROUP_SENTINEL opens a
              create form for a secondary-only custom group instead. */}
          {editingGroup && (
            <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 mb-6">
              <h2 className="text-sm font-semibold text-white mb-4">
                {editingGroup === NEW_GROUP_SENTINEL ? "New Custom Group (secondary-only)" : `Edit Group: ${editingGroup}`}
              </h2>
              {editingGroup === NEW_GROUP_SENTINEL && (
                <p className="text-xs text-amber-400/80 mb-4">
                  This group is not wired into any typed deal collection. It can only be attached to a deal as a
                  <em> secondary</em> category — it can never be selected as a deal's primary group.
                </p>
              )}
              <form onSubmit={(e) => void handleSubmitGroup(e)} className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  {editingGroup === NEW_GROUP_SENTINEL && (
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Slug *</label>
                      <input value={groupForm.slug} onChange={(e) => setGroupForm((f) => ({ ...f, slug: e.target.value }))}
                        placeholder="e.g. honeymoon-specials" className={inputCls} />
                    </div>
                  )}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Display label *</label>
                    <input value={groupForm.label} onChange={(e) => setGroupForm((f) => ({ ...f, label: e.target.value }))}
                      placeholder="e.g. Stays" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Icon (emoji)</label>
                    <input value={groupForm.icon} onChange={(e) => setGroupForm((f) => ({ ...f, icon: e.target.value }))}
                      placeholder="e.g. 🏨" className={inputCls} />
                  </div>
                  {groupForm.is_priority_fixed && (() => {
                    const pseudoCat: Category = {
                      id: undefined,
                      slug: editingGroup === NEW_GROUP_SENTINEL ? groupForm.slug : (editingGroup ?? groupForm.slug),
                      name: groupForm.label, display_group: groupForm.label, group_slug: null, parent_slug: null,
                      level: 1, item_types: [], is_promotional: false, description: null, keywords: [],
                      icon: null, image_url: null, video_url: null, is_active: groupForm.is_active,
                      is_promoted: groupForm.is_promoted, sort_order: groupForm.sort_order, is_priority_fixed: true,
                    };
                    const slots = availablePrioritySlots(pseudoCat, categories);
                    return (
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Priority</label>
                        <select value={groupForm.sort_order}
                          onChange={(e) => setGroupForm((f) => ({ ...f, sort_order: Number(e.target.value) }))} className={inputCls}>
                          {slots.includes(groupForm.sort_order) ? null : (
                            <option value={groupForm.sort_order}>{groupForm.sort_order}</option>
                          )}
                          {slots.map((n) => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })()}
                </div>
                {editingGroup !== NEW_GROUP_SENTINEL && (
                <div className="flex items-end gap-4">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-400 mb-1">Banner image, GIF, or video (thumbnail auto-generated from first frame)</label>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
                      disabled={saving === `${editingGroup}-media`}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file && editingGroup) void handleGroupMediaUpload(editingGroup, file);
                        e.target.value = "";
                      }}
                      className="w-full text-xs text-gray-400 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-indigo-600 file:text-white file:text-xs file:cursor-pointer"
                    />
                    <p className="text-xs text-gray-500 mt-1">Save the group first, then edit it again to add a banner.</p>
                  </div>
                  {groupForm.image_url && (
                    <button
                      type="button"
                      onClick={() => setPreviewMedia({ imageUrl: groupForm.image_url, videoUrl: groupForm.video_url })}
                      className="relative w-16 h-16 rounded-lg border border-gray-700 overflow-hidden shrink-0"
                    >
                      <img src={groupForm.image_url} alt="Current banner" className="w-full h-full object-cover" />
                      {groupForm.video_url && (
                        <span className="absolute inset-0 flex items-center justify-center bg-black/30 text-white text-lg">▶</span>
                      )}
                    </button>
                  )}
                </div>
                )}
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                    <input type="checkbox" checked={groupForm.is_active}
                      onChange={(e) => setGroupForm((f) => ({ ...f, is_active: e.target.checked }))}
                      className="w-4 h-4 rounded accent-indigo-500" />
                    Active
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                    <input type="checkbox" checked={groupForm.is_promoted}
                      onChange={(e) => setGroupForm((f) => ({ ...f, is_promoted: e.target.checked }))}
                      className="w-4 h-4 rounded accent-amber-500" />
                    Promoted
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                    <input type="checkbox" checked={groupForm.is_priority_fixed}
                      onChange={(e) => setGroupForm((f) => ({ ...f, is_priority_fixed: e.target.checked }))}
                      className="w-4 h-4 rounded accent-amber-500" />
                    Fix priority (excludes this group from random shuffle)
                  </label>
                </div>
                <div className="flex gap-3">
                  <button type="submit" disabled={saving === "groupform"}
                    className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-medium transition-colors disabled:opacity-50">
                    {saving === "groupform" ? "Saving…" : "Save Group"}
                  </button>
                  <button type="button" onClick={handleCancelGroup}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-xl text-sm transition-colors">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden mb-6">
            <div className="px-5 py-3 border-b border-gray-800">
              <h2 className="text-sm font-semibold text-white">
                Groups <span className="text-gray-500 font-normal ml-1">({groupRows.length})</span>
              </h2>
              <p className="text-gray-500 text-xs mt-1">
                These are the typed-routable groups (plus 3 editorial ones). Each maps to a typed deal
                collection, so this list is locked — you can rename/style them but not add to it here.
              </p>
            </div>
            {loading ? (
              <div className="px-5 py-8 text-center text-gray-500 text-sm"><Spinner /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-800">
                    <tr>
                      <th className="text-left px-5 py-3 text-gray-500 font-medium text-xs">Group</th>
                      <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Slug</th>
                      <th className="text-center px-4 py-3 text-gray-500 font-medium text-xs">Sort</th>
                      <th className="text-center px-4 py-3 text-gray-500 font-medium text-xs">Subcategories</th>
                      <th className="text-center px-4 py-3 text-gray-500 font-medium text-xs">Active</th>
                      <th className="text-center px-4 py-3 text-gray-500 font-medium text-xs">Promoted</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/50">
                    {groupRows.map((g) => (
                      <tr key={g.slug} className="hover:bg-gray-800/20 transition-colors">
                        <td className="px-5 py-3 text-white text-sm font-medium">
                          <span className="mr-1">{g.icon}</span>{g.label}
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs font-mono">{g.slug}</td>
                        <td className="px-4 py-3 text-center text-gray-300 text-xs">{g.sort_order}</td>
                        <td className="px-4 py-3 text-center text-gray-300 text-xs">{g.memberCount}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${g.is_active ? "bg-green-900/40 text-green-400" : "bg-gray-800 text-gray-500"}`}>
                            {g.is_active ? "On" : "Off"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {g.is_promoted && (
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-900/40 text-amber-400">
                              Promoted
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end">
                            <button onClick={() => handleEditGroup(g)}
                              className="px-3 py-1 bg-indigo-900/40 hover:bg-indigo-900/70 text-indigo-300 rounded-lg text-xs transition-colors">
                              Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Custom (secondary-only) groups */}
          <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-800 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-white">
                  Custom Groups <span className="text-gray-500 font-normal ml-1">({customGroupRows.length})</span>
                </h2>
                <p className="text-gray-500 text-xs mt-1">
                  Admin-created groups. Not wired to a typed deal collection — deals can only attach these as
                  secondary categories, never as their primary group.
                </p>
              </div>
              <button onClick={handleNewGroup}
                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-xs font-medium text-white transition-colors whitespace-nowrap">
                + New Group
              </button>
            </div>
            {customGroupRows.length === 0 ? (
              <div className="px-5 py-8 text-center text-gray-500 text-sm">
                No custom groups yet. Create one, or promote a subcategory from the Subcategories tab.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-800">
                    <tr>
                      <th className="text-left px-5 py-3 text-gray-500 font-medium text-xs">Group</th>
                      <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Slug</th>
                      <th className="text-center px-4 py-3 text-gray-500 font-medium text-xs">Sort</th>
                      <th className="text-center px-4 py-3 text-gray-500 font-medium text-xs">Subcategories</th>
                      <th className="text-center px-4 py-3 text-gray-500 font-medium text-xs">Active</th>
                      <th className="text-center px-4 py-3 text-gray-500 font-medium text-xs">Promoted</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/50">
                    {customGroupRows.map((g) => (
                      <tr key={g.slug} className="hover:bg-gray-800/20 transition-colors">
                        <td className="px-5 py-3 text-white text-sm font-medium">
                          <span className="mr-1">{g.icon}</span>{g.label}
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs font-mono">{g.slug}</td>
                        <td className="px-4 py-3 text-center text-gray-300 text-xs">{g.sort_order}</td>
                        <td className="px-4 py-3 text-center text-gray-300 text-xs">{g.memberCount}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${g.is_active ? "bg-green-900/40 text-green-400" : "bg-gray-800 text-gray-500"}`}>
                            {g.is_active ? "On" : "Off"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button onClick={() => void handleToggleGroupPromoted(g)} disabled={saving === g.slug + "group-is_promoted"}
                            className={`text-xs font-semibold px-2 py-0.5 rounded-full transition-colors ${g.is_promoted ? "bg-amber-900/40 text-amber-400" : "bg-gray-800 text-gray-500"}`}>
                            {g.is_promoted ? "On" : "Off"}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2 justify-end">
                            <button onClick={() => handleEditGroup(g)}
                              className="px-3 py-1 bg-indigo-900/40 hover:bg-indigo-900/70 text-indigo-300 rounded-lg text-xs transition-colors">
                              Edit
                            </button>
                            <button
                              onClick={() => { setDemoteTarget({ slug: g.anchorSlug ?? g.slug, label: g.label }); setDemoteDestGroup(""); }}
                              title="Turn this group back into a plain subcategory"
                              className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-xs transition-colors">
                              Reset to Subcategory
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Create / Edit subcategory form */}
          <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 mb-6">
            <h2 className="text-sm font-semibold text-white mb-4">
              {editingSlug ? `Edit Subcategory: ${editingSlug}` : "Add New Subcategory"}
            </h2>
            <form onSubmit={(e) => void handleSubmitSub(e)} className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Slug *</label>
                  <input value={subForm.slug} disabled={!!editingSlug}
                    onChange={(e) => setSubForm((f) => ({ ...f, slug: e.target.value }))}
                    placeholder="e.g. hostels"
                    className={`${inputCls} disabled:opacity-50`} />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Name *</label>
                  <input value={subForm.name} onChange={(e) => setSubForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Hostels" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Icon (emoji)</label>
                  <input value={subForm.icon} onChange={(e) => setSubForm((f) => ({ ...f, icon: e.target.value }))}
                    placeholder="e.g. 🛏️" className={inputCls} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Group *</label>
                  <select value={subForm.group_slug}
                    onChange={(e) => setSubForm((f) => ({ ...f, group_slug: e.target.value }))} className={inputCls}>
                    <option value="">Select a group…</option>
                    {CANONICAL_GROUPS.map((g) => (
                      <option key={g.slug} value={g.slug}>
                        {findAnchor(g.slug)?.display_group || g.label} ({g.slug})
                      </option>
                    ))}
                  </select>
                </div>
                {subForm.is_priority_fixed && (() => {
                  const pseudoCat: Category = {
                    id: undefined, slug: editingSlug || subForm.slug, name: subForm.name,
                    group_slug: subForm.group_slug || null, display_group: null, parent_slug: null,
                    level: 2, item_types: [], is_promotional: false, description: null, keywords: [],
                    icon: null, image_url: null, video_url: null, is_active: subForm.is_active,
                    is_promoted: subForm.is_promoted, sort_order: subForm.sort_order, is_priority_fixed: true,
                  };
                  const slots = availablePrioritySlots(pseudoCat, categories);
                  return (
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Priority</label>
                      <select value={subForm.sort_order}
                        onChange={(e) => setSubForm((f) => ({ ...f, sort_order: Number(e.target.value) }))} className={inputCls}>
                        {slots.includes(subForm.sort_order) ? null : (
                          <option value={subForm.sort_order}>{subForm.sort_order}</option>
                        )}
                        {slots.map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                  );
                })()}
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                  <input type="checkbox" checked={subForm.is_priority_fixed}
                    onChange={(e) => setSubForm((f) => ({ ...f, is_priority_fixed: e.target.checked }))}
                    className="w-4 h-4 rounded accent-amber-500" />
                  Fix priority (excludes this subcategory from random shuffle)
                </label>
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-1">Description</label>
                <textarea rows={2} value={subForm.description}
                  onChange={(e) => setSubForm((f) => ({ ...f, description: e.target.value }))} className={inputCls} />
              </div>

              <div className="flex items-end gap-4">
                <div className="flex-1">
                  <label className="block text-xs text-gray-400 mb-1">Banner image, GIF, or video (thumbnail auto-generated from first frame)</label>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
                    disabled={editingSlug ? saving === `${editingSlug}-media` : false}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (editingSlug) void handleSubMediaUpload(editingSlug, file);
                        else setPendingSubMediaFile(file);
                      }
                      e.target.value = "";
                    }}
                    className="w-full text-xs text-gray-400 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-indigo-600 file:text-white file:text-xs file:cursor-pointer"
                  />
                  {!editingSlug && pendingSubMediaFile && (
                    <p className="text-xs text-gray-500 mt-1">
                      Selected: {pendingSubMediaFile.name} (uploads after you create the subcategory)
                    </p>
                  )}
                </div>
                {editingSlug && subForm.image_url && (
                  <button
                    type="button"
                    onClick={() => setPreviewMedia({ imageUrl: subForm.image_url, videoUrl: subForm.video_url })}
                    className="relative w-16 h-16 rounded-lg border border-gray-700 overflow-hidden shrink-0"
                  >
                    <img src={subForm.image_url} alt="Current banner" className="w-full h-full object-cover" />
                    {subForm.video_url && (
                      <span className="absolute inset-0 flex items-center justify-center bg-black/30 text-white text-lg">▶</span>
                    )}
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Item types</label>
                  <div className="flex flex-wrap gap-1.5">
                    {ITEM_TYPES.map((it) => {
                      const on = subForm.item_types.includes(it);
                      return (
                        <button type="button" key={it}
                          onClick={() => setSubForm((f) => ({
                            ...f,
                            item_types: on ? f.item_types.filter((x) => x !== it) : [...f.item_types, it],
                          }))}
                          className={`px-2 py-1 rounded-lg text-xs border transition-colors ${
                            on
                              ? "bg-indigo-600 border-indigo-500 text-white"
                              : "bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600"
                          }`}>
                          {it}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Keywords (comma-separated)</label>
                  <input value={subForm.keywords} onChange={(e) => setSubForm((f) => ({ ...f, keywords: e.target.value }))}
                    placeholder="e.g. hostel, dorm, backpacker" className={inputCls} />
                </div>
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                  <input type="checkbox" checked={subForm.is_active}
                    onChange={(e) => setSubForm((f) => ({ ...f, is_active: e.target.checked }))}
                    className="w-4 h-4 rounded accent-indigo-500" />
                  Active
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                  <input type="checkbox" checked={subForm.is_promoted}
                    onChange={(e) => setSubForm((f) => ({ ...f, is_promoted: e.target.checked }))}
                    className="w-4 h-4 rounded accent-amber-500" />
                  Promoted
                </label>
              </div>

              <div className="flex gap-3">
                <button type="submit" disabled={saving === "subform"}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-medium transition-colors disabled:opacity-50">
                  {saving === "subform" ? "Saving…" : editingSlug ? "Update Subcategory" : "Add Subcategory"}
                </button>
                {editingSlug && (
                  <button type="button" onClick={handleNewSub}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-xl text-sm transition-colors">
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Subcategories table */}
          <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-800 flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold text-white whitespace-nowrap">
                Subcategories <span className="text-gray-500 font-normal ml-1">({filteredSubs.length})</span>
              </h2>
              <div className="flex gap-2">
                <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}
                  className="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/50">
                  <option value="">All groups</option>
                  {CANONICAL_GROUPS.map((g) => (
                    <option key={g.slug} value={g.slug}>{findAnchor(g.slug)?.display_group || g.label}</option>
                  ))}
                </select>
                <select value={promotedFilter} onChange={(e) => setPromotedFilter(e.target.value as typeof promotedFilter)}
                  className="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/50">
                  <option value="">All (promoted or not)</option>
                  <option value="promoted">Promoted only</option>
                  <option value="not_promoted">Not promoted</option>
                </select>
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…"
                  className="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-xs w-48 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 placeholder-gray-500" />
              </div>
            </div>

            {selectedSlugs.size > 0 && (
              <div className="px-5 py-2.5 border-b border-gray-800 bg-gray-800/40 flex items-center gap-3">
                <span className="text-xs text-gray-400">{selectedSlugs.size} selected</span>
                <button onClick={() => void handleBulkSetPromoted(true)} disabled={saving === "bulk-promote"}
                  className="px-3 py-1 bg-amber-900/40 hover:bg-amber-900/70 text-amber-300 rounded-lg text-xs transition-colors disabled:opacity-50">
                  Promote selected
                </button>
                <button onClick={() => void handleBulkSetPromoted(false)} disabled={saving === "bulk-promote"}
                  className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-xs transition-colors disabled:opacity-50">
                  Unpromote selected
                </button>
                <button onClick={() => setSelectedSlugs(new Set())}
                  className="text-xs text-gray-500 hover:text-gray-300 ml-auto">
                  Clear selection
                </button>
              </div>
            )}

            {loading ? (
              <div className="px-5 py-8 text-center text-gray-500 text-sm"><Spinner /></div>
            ) : filteredSubs.length === 0 ? (
              <div className="px-5 py-8 text-center text-gray-500 text-sm">No subcategories found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-800">
                    <tr>
                      <th className="px-5 py-3">
                        <input
                          type="checkbox"
                          checked={filteredSubs.length > 0 && filteredSubs.every((c) => selectedSlugs.has(c.slug))}
                          onChange={(e) =>
                            setSelectedSlugs(e.target.checked ? new Set(filteredSubs.map((c) => c.slug)) : new Set())
                          }
                          className="w-4 h-4 rounded accent-indigo-500"
                        />
                      </th>
                      <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Subcategory</th>
                      <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Slug</th>
                      <th className="text-left px-4 py-3 text-gray-500 font-medium text-xs">Group</th>
                      <th className="text-center px-4 py-3 text-gray-500 font-medium text-xs">Active</th>
                      <th className="text-center px-4 py-3 text-gray-500 font-medium text-xs">Promoted</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/50">
                    {filteredSubs.map((cat) => (
                      <tr key={cat.slug} className="hover:bg-gray-800/20 transition-colors">
                        <td className="px-5 py-3">
                          <input
                            type="checkbox"
                            checked={selectedSlugs.has(cat.slug)}
                            onChange={() => toggleSelected(cat.slug)}
                            className="w-4 h-4 rounded accent-indigo-500"
                          />
                        </td>
                        <td className="px-4 py-3 text-white text-sm font-medium">
                          <span className="mr-1">{cat.icon}</span>{cat.name}
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs font-mono">{cat.slug}</td>
                        <td className="px-4 py-3 text-gray-400 text-xs">
                          {cat.display_group ?? normGroup(cat.group_slug) ?? <span className="text-gray-600">—</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button onClick={() => void handleToggleActive(cat)} disabled={saving === cat.slug + "is_active"}
                            className={`text-xs font-semibold px-2 py-0.5 rounded-full transition-colors ${cat.is_active ? "bg-green-900/40 text-green-400" : "bg-gray-800 text-gray-500"}`}>
                            {cat.is_active ? "On" : "Off"}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button onClick={() => void handleTogglePromoted(cat)} disabled={saving === cat.slug + "is_promoted"}
                            className={`text-xs font-semibold px-2 py-0.5 rounded-full transition-colors ${cat.is_promoted ? "bg-amber-900/40 text-amber-400" : "bg-gray-800 text-gray-500"}`}>
                            {cat.is_promoted ? "On" : "Off"}
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleToggleFixedPriority(cat)}
                            disabled={saving === cat.slug + "is_priority_fixed"}
                            className={`ml-2 px-2 py-1 rounded text-xs ${cat.is_priority_fixed ? "bg-amber-600 text-white" : "bg-gray-700 text-gray-300"}`}
                          >
                            {cat.is_priority_fixed ? "Priority Fixed" : "Fix Priority"}
                          </button>
                          {cat.is_priority_fixed && (
                            <select
                              value={cat.sort_order}
                              onChange={(e) => void handleSetFixedPriority(cat, Number(e.target.value))}
                              disabled={saving === cat.slug + "sort_order"}
                              className="ml-2 text-xs bg-gray-800 border border-gray-600 rounded px-1 py-1"
                            >
                              {availablePrioritySlots(cat, categories).includes(cat.sort_order) ? null : (
                                <option value={cat.sort_order}>{cat.sort_order}</option>
                              )}
                              {availablePrioritySlots(cat, categories).map((n) => (
                                <option key={n} value={n}>{n}</option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2 justify-end">
                            <button onClick={() => handleEditSub(cat)}
                              className="px-3 py-1 bg-indigo-900/40 hover:bg-indigo-900/70 text-indigo-300 rounded-lg text-xs transition-colors">
                              Edit
                            </button>
                            <button onClick={() => { setTab("groups"); handlePromoteToGroup(cat); }}
                              title="Turn this subcategory into its own secondary-only group"
                              className="px-3 py-1 bg-amber-900/30 hover:bg-amber-900/60 text-amber-400 rounded-lg text-xs transition-colors">
                              Promote to Group
                            </button>
                            <button onClick={() => setDeleteTarget(cat)} disabled={saving === cat.slug}
                              className="px-3 py-1 bg-red-900/30 hover:bg-red-900/60 text-red-400 rounded-lg text-xs transition-colors disabled:opacity-50">
                              {saving === cat.slug ? "…" : "Delete"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>

    {deleteTarget && (
      <ConfirmModal
        title="Delete Subcategory"
        description={`"${deleteTarget.name}" will be permanently removed from the database. This cannot be undone.`}
        items={["Removes the category document from MongoDB", "Existing deals that reference this slug will lose their category"]}
        confirmLabel="Delete permanently"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    )}

    {demoteTarget && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6" onClick={() => setDemoteTarget(null)}>
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
          <h2 className="text-sm font-semibold text-white mb-2">Reset "{demoteTarget.label}" to a Subcategory</h2>
          <p className="text-gray-500 text-xs mb-4">
            This group anchor will become a plain subcategory nested under the group you pick below. Pick the
            group it originally belonged to (or any other), then confirm.
          </p>
          <label className="block text-xs text-gray-400 mb-1">Nest under group *</label>
          <select value={demoteDestGroup} onChange={(e) => setDemoteDestGroup(e.target.value)} className={inputCls}>
            <option value="">Select a group…</option>
            {CANONICAL_GROUPS.map((g) => (
              <option key={g.slug} value={g.slug}>{findAnchor(g.slug)?.display_group || g.label} ({g.slug})</option>
            ))}
            {customGroupRows
              .filter((g) => g.slug !== demoteTarget.slug)
              .map((g) => (
                <option key={g.slug} value={g.slug}>{g.label} ({g.slug})</option>
              ))}
          </select>
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => void handleDemoteToSubcategory()}
              disabled={!demoteDestGroup || saving === "demote"}
              className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-medium transition-colors disabled:opacity-50">
              {saving === "demote" ? "Saving…" : "Confirm Reset"}
            </button>
            <button onClick={() => setDemoteTarget(null)} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-xl text-sm transition-colors">
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}

    {previewMedia && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6"
        onClick={() => setPreviewMedia(null)}
      >
        <div className="relative max-w-3xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => setPreviewMedia(null)}
            className="absolute -top-10 right-0 text-gray-300 hover:text-white text-sm"
          >
            Close ✕
          </button>
          {previewMedia.videoUrl ? (
            <video src={previewMedia.videoUrl} controls autoPlay className="max-w-full max-h-[85vh] rounded-xl" />
          ) : (
            previewMedia.imageUrl && (
              <img src={previewMedia.imageUrl} alt="Media preview" className="max-w-full max-h-[85vh] rounded-xl" />
            )
          )}
        </div>
      </div>
    )}
    </>
  );
}
